import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { landingPagePath, registrationPagePath } from '../navigation/paths';
import { useContracts } from 'components/contracts';
import { currentBrand, chatbotCssUrls } from 'config';
import { Chatbot } from '@cp-shared-15/frontend-ui';
import { useMyProfile } from '../my-profile';
import { getCommonProductField } from './chatbotContractType';

const getChatbotCssUrl = () => {
    const brandKey = currentBrand?.toLowerCase() as keyof typeof chatbotCssUrls;
    return chatbotCssUrls[brandKey] || chatbotCssUrls.vwfs;
};

const LandingPageChatbot: React.FC<{ mercuryApiKey: string; mercuryCssUrl: string }> = ({
    mercuryApiKey,
    mercuryCssUrl,
}) => {
    useEffect(() => {
        if (document.querySelector('script#mercury-widget-snippet')) return;

        const config = {
            userId:
                Math.random()
                    .toString(36)
                    .substring(2, 15) +
                Math.random()
                    .toString(36)
                    .substring(2, 15),
            context: {
                'global.productType': 'LandingPage',
                'user.firstName': 'LandingPage',
                'user.lastName': 'LandingPage',
                'user.email': 'LandingPage',
                'global.customerNumber': 'LandingPage',
            },
        };

        const configScript = document.createElement('script');
        configScript.type = 'text/javascript';
        configScript.text = `window.mercuryUser = ${JSON.stringify(config)};`;

        const widgetScript = document.createElement('script');
        widgetScript.type = 'application/javascript';
        widgetScript.src = 'https://cdn.mercury.ai/widget/v4/widget.js';
        widgetScript.async = true;
        widgetScript.id = 'mercury-widget-snippet';
        widgetScript.setAttribute('data-key', mercuryApiKey);

        document.body.appendChild(widgetScript);
        document.body.appendChild(configScript);

        return () => {
            document.getElementById('mercury-widget')?.remove();
            document.querySelector('script#mercury-widget-snippet')?.remove();
        };
    }, []);

    return (
        <div data-testid="mercury-widget-snippet">
            <link rel="stylesheet" type="text/css" href={mercuryCssUrl} />
        </div>
    );
};

export const ChatbotWrapper: React.FC = () => {
    const location = useLocation();
    const isRegistrationPage = location.pathname === registrationPagePath();
    const isLandingPage = location.pathname === landingPagePath();

    const { data: contracts, hasReceivedResponse: hasReceivedContracts } = useContracts();
    const { data: myProfile, hasReceivedResponse: hasReceivedProfile } = useMyProfile();

    if (isLandingPage) {
        return (
            <LandingPageChatbot
                mercuryApiKey={process.env.REACT_APP_CHATBOT_LANDING_PAGE_API_KEY || ''}
                mercuryCssUrl={getChatbotCssUrl()}
            />
        );
    }

    if (!hasReceivedProfile || !hasReceivedContracts) {
        return null;
    }

    const productType = getCommonProductField('contractType', contracts) || 'None';

    const buildConfigContext = () => {
        if (isRegistrationPage) {
            return {
                'global.productType': 'KYC',
                'user.firstName': 'KYC',
                'user.lastName': 'KYC',
                'user.email': 'KYC',
                'global.customerNumber': 'KYC',
            };
        }

        if (!myProfile || !contracts) {
            return null;
        }

        return {
            'global.productType': productType,
            'user.firstName': myProfile.identification?.firstName || '',
            'user.lastName': myProfile.identification?.lastName || '',
            'user.email': myProfile.contactDetails?.email || '',
            'global.customerNumber': myProfile.identification?.id || '',
        };
    };

    const context = buildConfigContext();
    if (!context) return null;

    const chatbotApiKey = isRegistrationPage
        ? process.env.REACT_APP_CHATBOT_REGISTRATION_API_KEY
        : process.env.REACT_APP_CHATBOT_API_KEY;

    return <Chatbot context={context} mercuryCssUrl={getChatbotCssUrl()} mercuryApiKey={chatbotApiKey || ''} />;
};
